import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, signal, computed } from '@angular/core';

export interface SidebarItem {
  id: string;
  icon: string;
  label: string;
  route?: string;
}

/**
 * Collapsible navigation sidebar. Expands on hover and collapses on leave;
 * a pin toggle keeps it expanded permanently — the choice is persisted to
 * localStorage so it survives reloads. Highlights the active route item.
 */
@Component({
  selector: 'es-sidebar',
  imports: [CommonModule],
  template: `
    <aside
      [class]="sidebarClasses()"
      (mouseenter)="hovered.set(true)"
      (mouseleave)="hovered.set(false)"
    >
      <div class="flex flex-col h-full py-3">
        <!-- Brand + pin toggle -->
        <div class="flex items-center gap-3 px-4 mb-6">
          <span class="text-echo-blue text-2xl shrink-0">◈</span>
          @if (!collapsed()) {
            <span class="text-lg font-bold text-[var(--text)] whitespace-nowrap">EchoSpaces</span>
            <button
              type="button"
              [class]="pinButtonClasses()"
              (click)="togglePinned()"
              [attr.aria-pressed]="pinned()"
              [attr.aria-label]="pinned() ? 'Unpin sidebar' : 'Pin sidebar'"
              [title]="pinned() ? 'Unpin sidebar' : 'Pin sidebar'"
            >📌</button>
          }
        </div>

        <!-- Nav items -->
        <nav class="flex-1 flex flex-col gap-1 px-2">
          @for (item of items; track item.id) {
            <button
              [class]="itemClasses(item)"
              (click)="itemClicked.emit(item)"
            >
              <span class="text-lg w-6 text-center shrink-0">{{ item.icon }}</span>
              @if (!collapsed()) {
                <span class="whitespace-nowrap">{{ item.label }}</span>
              }
            </button>
          }
        </nav>
      </div>
    </aside>
  `,
})
export class SidebarComponent {
  /** localStorage key for the persisted pin choice. */
  private static readonly PIN_STORAGE_KEY = 'es-sidebar-pinned';

  @Input() items: SidebarItem[] = [];
  @Input() activeId: string | null = null;
  @Output() itemClicked = new EventEmitter<SidebarItem>();

  /** True while the pointer is over the sidebar. */
  readonly hovered = signal(false);

  /** True when the user has pinned the sidebar open — persisted across reloads. */
  readonly pinned = signal(SidebarComponent.readPinned());

  /** Effective state: collapsed unless pinned open or currently hovered. */
  readonly collapsed = computed(() => !this.pinned() && !this.hovered());

  readonly sidebarClasses = computed(() => {
    const base = 'h-screen bg-[var(--bg-card)] border-r border-[var(--border)] transition-all duration-200 overflow-hidden shrink-0';
    const width = this.collapsed() ? 'w-14' : 'w-56';
    return `${base} ${width}`;
  });

  /** Pin glyph: accent + upright when pinned, dimmed + tilted when loose. */
  readonly pinButtonClasses = computed(() => {
    const base = 'ml-auto text-sm leading-none cursor-pointer transition-all duration-200';
    const state = this.pinned()
      ? 'text-echo-blue'
      : 'text-[var(--text-secondary)] hover:text-[var(--text)] opacity-70 rotate-45';
    return `${base} ${state}`;
  });

  /** Toggle the pinned state and persist it to localStorage. */
  togglePinned(): void {
    const next = !this.pinned();
    this.pinned.set(next);
    try {
      globalThis.localStorage?.setItem(SidebarComponent.PIN_STORAGE_KEY, String(next));
    } catch {
      // localStorage unavailable (private browsing / SSR) — the pin still
      // holds for this session, it just won't survive a reload.
    }
  }

  itemClasses(item: SidebarItem) {
    const base = 'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer';
    const active = this.activeId === item.id
      ? 'bg-echo-blue/10 text-echo-blue'
      : 'text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)]';
    return `${base} ${active}`;
  }

  private static readPinned(): boolean {
    try {
      return globalThis.localStorage?.getItem(SidebarComponent.PIN_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }
}
